import React from 'react';
import { Button } from 'antd';
import { TFunction } from 'i18next';
import { IDocument, IFolder } from 'interfaces';
import classes from './Agreement.module.scss';
import { CheckCircleOutlined } from '@ant-design/icons';
import api from 'api';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setAgreementState } from 'store/slices/dataAgreementsSlice';
import { setDocuments, setSelectedDocument } from 'store/slices/dataDocumentsSlice';
import dayjs from 'dayjs';

type AgreementButtonsProps = {
  selectedDocument: IDocument | null;
  selectedFolder: IFolder | null;
  documentPermissions: string[];
  onDeclineOpen: () => void;
  t: TFunction;
};

export default function AgreementButtons({ t, onDeclineOpen, documentPermissions }: AgreementButtonsProps) {  
  const [isChangingStatus, setIsChangingStatus] = React.useState(false);

  const { myAgreement, agreements, needAgreementFromUsers } = useAppSelector((store) => store.agreements);
  const { selectedDocument, selectedFolder } = useAppSelector((state) => state.documents);
  const { documents, folders } = useAppSelector((store) => store.documents);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { messageApi } = useAppSelector((store) => store.windowState);

  const dispatch = useAppDispatch();
  
  const changeStatusHandle = async () => {
    setIsChangingStatus(true);
    try {
      await api.changeAgreementStatus(
        {
          id: String(selectedFolder ? selectedFolder.id : selectedDocument?.id),
          entity: selectedFolder ? 'folder' : 'document'
        },
        dataRoom?.id!,
        selectedFolder ? selectedFolder?.id! : selectedDocument?.id!
      );

      const changedMyAgreement = myAgreement
        ? {...myAgreement, agreement_state: !myAgreement.agreement_state, updated_at: dayjs().utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS')}
        : null
      const newNeedAgreementFromUsers = needAgreementFromUsers.map(agreementFromUsers => 
        (agreementFromUsers.user_id === changedMyAgreement?.user_id ? changedMyAgreement : agreementFromUsers)
      );

      const newAgreements = agreements.map(agreement => ({
        ...agreement,
        users: agreement.users?.map(user => user.user_id === changedMyAgreement?.user_id ? changedMyAgreement : user)
      }))

      dispatch(setAgreementState({
        myAgreement: changedMyAgreement,
        needAgreementFromUsers: newNeedAgreementFromUsers,
        agreements: newAgreements,
      }));

      // логика для апрува одним юзером из группы
      // const isApprovedDoc = !newAgreements.map(group => {
      //   const approvedUser = group.users?.find(user => user.agreement_state === true)
      //   return Boolean(approvedUser)
      // }).includes(false)

      // логика для апрува всеми юзерами
      const unchanchedStatus = newNeedAgreementFromUsers.find((agreement) => agreement.agreement_state === null);
      const declinedStatus = newNeedAgreementFromUsers.find((agreement) => agreement.agreement_state === false);

      selectedFolder
        ? dispatch(setDocuments({documents, folders: folders.map(folder => {
          return folder.id === selectedFolder.id
            // логика для апрува всеми юзерами
            ? { ...folder, agreement_status: declinedStatus ? 1 : unchanchedStatus ? 0 : 2 }
            // логика для апрува одним юзером из группы
            // ? { ...folder, agreement_status: declinedStatus ? 1 : !isApprovedDoc ? 0 : 2 }
            : folder
        })}))
        : dispatch(
          setDocuments({
            documents: documents.map((document) => {
              return document.id === selectedDocument!.id
                // логика для апрува всеми юзерами
                ? { ...document, agreement_status: declinedStatus ? 1 : unchanchedStatus ? 0 : 2 }
                // логика для апрува одним юзером из группы
                // ? {
                //     ...document,
                //     agreement_status: declinedStatus ? 1 : !isApprovedDoc ? 0 : 2,
                //     permissions: documentPermissions, // Это зачем?
                //   }
                : document;
            }),
            folders,
          })
        );
      // логика для апрува всеми юзерами
      dispatch(setSelectedDocument({...selectedDocument!, agreement_status: declinedStatus ? 1 : unchanchedStatus ? 0 : 2 }))
      // логика для апрува одним юзером из группы
      // dispatch(setSelectedDocument({ ...selectedDocument!, agreement_status: declinedStatus ? 1 : !isApprovedDoc ? 0 : 2 }))

      myAgreement?.agreement_state
        ? messageApi?.success(t('Documents.details.agreement.agreementSuccessfully'))
        : messageApi?.success(t('Documents.details.agreement.canceledSuccessfully'));
    } catch {
      myAgreement?.agreement_state
        ? messageApi?.error(t('Documents.details.agreement.canceledError'))
        : messageApi?.error(t('Documents.details.agreement.agreementError'));
    } finally {
      setIsChangingStatus(false);
    }
  }

  const isStatusNoZero = selectedDocument ? selectedDocument.agreement_status !== 0 : selectedFolder?.agreement_status !== 0;
  const isRejectedApprove = Boolean(needAgreementFromUsers.find(agreement => agreement.agreement_state === false));
  const isAgreementstatusNotNull = Boolean(myAgreement?.agreement_state);

  const isDisableButtons = isStatusNoZero || isRejectedApprove || isAgreementstatusNotNull
  
  return (
    <div className={classes.agreement_buttons}>
      <Button
        onClick={changeStatusHandle}
        type='primary'
        loading={isChangingStatus && !myAgreement?.agreement_state}
        disabled={isDisableButtons}
        icon={<CheckCircleOutlined className={classes.lightGreen}/>}
      >
        {myAgreement?.agreement_state ? t('Documents.details.agreement.agreed') : t('Documents.details.agreement.agree')}
      </Button>
      <Button
        onClick={onDeclineOpen}
        variant='solid'
        color='danger' 
        disabled={isDisableButtons}
        loading={isChangingStatus && Boolean(myAgreement?.agreement_state)}
      >
        {t('Documents.details.agreement.cancel')}
      </Button>
    </div>
  )
}
